import { api } from './client.js';

/** 원본 offline_timetable_input.php */
export interface TimetableItem {
  slot: string;
  isdone: string;
  memo: string;
  at: string;
}

export interface Timetable {
  seq: number;
  place: string;
  workdate: string;
  worker1: string;
  worker2: string;
  notDoneBox: string;
  inboxTime: string;
  boxCleanup: string;
  items: TimetableItem[];
}

export interface OfflineMeta {
  slots: string[];
  places: { code: string; label: string }[];
  saleHours: string[];
  saleGroups: string[];
  saleColumns: string[];
  /** 판매통계(bnn_offline.place)의 매장 - 타임테이블과 값 공간이 다르다(한글 이름). */
  salePlaces: string[];
  /** 그중 손으로 넣을 수 있는 매장 (노원무인은 배치가 채운다). */
  saleInputPlaces: string[];
  /**
   * 음수를 넣어도 되는 결제 칸. 지금은 결제 칸 8개(현금·카드·계좌이체·적립금·
   * 퀵·직수령·결제수단 합계·지출) 전부다. DB 컬럼이 unsigned 로 바뀌면(sql_mode 가
   * STRICT 가 아니라 음수를 넣어도 서버가 조용히 0 으로 돌린다) 그 칸만
   * 빠진다 - 화면은 이 목록을 그대로 따라가고 하드코딩하지 않는다
   * (offline.service.ts 의 NEGATIVE_ALLOWED_PAY_FIELDS 참고).
   */
  negativeAllowedPayFields: string[];
}

/** 오프라인 판매통계 — 원본 offline_sale.php */
export interface SaleCounts {
  income: number;
  payment: number;
  byHour: { hour: string; income: number; payment: number }[];
}

export interface SaleRow {
  seq: number;
  saleDate: string;
  weekday: number;
  place: string;
  worker1: string;
  worker2: string;
  weather: string;
  intype: string;
  regdate: string;
  payTotal: number;
  payCash: number;
  payCard: number;
  /** 계좌이체 (2026-10-07 신설) */
  payTransfer: number;
  /** 적립금 - 계좌이체가 따로 생기기 전에는 이 칸에 둘이 섞여 있었다. */
  payPoint: number;
  payQuick: number;
  /** 직수령 (2026-10-07 신설) */
  payDirect: number;
  dailyExpense: number;
  /** 객단가. 방문팀이 0 이면 null. */
  perTeam: number | null;
  team: SaleCounts;
  man: SaleCounts;
  woman: SaleCounts;
}

export interface SaleTotals {
  payTotal: number;
  payCash: number;
  payCard: number;
  payPoint: number;
  payQuick: number;
  dailyExpense: number;
  incomeTeam: number;
  paymentTeam: number;
  incomeMan: number;
  paymentMan: number;
  incomeWoman: number;
  paymentWoman: number;
}

export interface SaleList {
  rows: SaleRow[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
  totals: SaleTotals;
}

export type BoxField = 'notDoneBox' | 'inboxTime' | 'boxCleanup';

/* ── 포스시스템 재고연동 ─────────────────────────────────────────────────── */

/** server/src/modules/offline/invenSync.service.ts 의 InvenSyncStatus 와 같다. */
export type InvenSyncStatus =
  | 'linked'
  | 'ready'
  /** POS 코드가 옛 상품코드였고, gcode 로 현재 상품을 찾아 연동할 수 있다. */
  | 'gcodeRemapped'
  /** 옛 코드를 가진 현재 상품이 여럿이라 못 고른다. */
  | 'gcodeAmbiguous'
  /** 현재 상품은 찾았지만 옵션코드가 옮겨지지 않는다 (옵션 id 는 상품별이다). */
  | 'gcodeOptionLost'
  | 'noProduct'
  | 'noOption'
  | 'noBarcode'
  | 'multiBarcode'
  | 'badFormat'
  /** POS BarCode 에 공백·탭이 섞여 있다. 공백을 떼면 상품이 맞아도 넣지 않는다. */
  | 'oddBarcode'
  /** POS BarCode 가 POS 코드가 아니라 xadmin 바코드 그 자체다 - 넣을 것이 없다. */
  | 'posIsXadminBarcode'
  | 'duplicatePos';

export interface PosStoreMeta {
  key: string;
  label: string;
  /** '09:00~24:00' */
  openHours: string;
  /** 지금 조회할 수 있는가 (서버가 KST 로 판단한 값이다). */
  open: boolean;
  /** 운영시간 외 안내 문구. */
  closedMessage: string;
  /** 접속 설정이 비어 있으면 그 이유. 없으면 null. */
  configProblem: string | null;
}

export interface InvenSyncMeta {
  /** 서버가 본 '지금' (KST). 화면 시계와 어긋날 때 판단 근거가 된다. */
  now: string;
  defaultStore: string;
  statusLabels: Record<InvenSyncStatus, string>;
  stores: PosStoreMeta[];
}

export interface InvenSyncRow {
  /** 양끝 공백을 떼어낸 값. 화면에 보여주는 값이다. */
  posBarcode: string;
  /** POS 에 저장된 그대로. UPDATE 의 WHERE 가 이 값을 쓴다 (공백·탭 포함). */
  posBarcodeStored: string;
  /** NULL 이면 null - 그 줄만 연동 대상이다. */
  barcode1: string | null;
  /** POS dbo.Goods.cate_cd (NULL 이면 null). 이것도 NULL 인 줄만 채운다. */
  cateCd: string | null;
  goodsCode: number | null;
  opCode: number | null;
  gname: string;
  opname: string;
  /**
   * 입력칸에 채워 넣을 **제안** 바코드. 저절로 저장되지는 않는다 - 사람이
   * 입력칸에 넣고 저장해야 들어간다.
   */
  xadminBarcode: string | null;
  /** POS 코드 안에 있던 옛 상품코드. gcode 로 옮겨진 줄에만 값이 있다. */
  oldGoodsCode: number | null;
  barcodeCount: number;
  status: InvenSyncStatus;
}

export interface InvenSyncList {
  store: string;
  /** POS 를 읽은 시각 (KST). */
  at: string;
  summary: { total: number; byStatus: Record<InvenSyncStatus, number> };
  rows: InvenSyncRow[];
  page: number;
  perPage: number;
  total: number;
  totalPages: number;
}

/** BarCode1 한 줄 수정 결과. */
export interface SetBarcode1Result {
  /** 'changedElsewhere' 면 그 사이 다른 사람이 고쳐서 아무것도 안 바뀌었다. */
  outcome: 'updated' | 'changedElsewhere';
  /** 저장 뒤 POS 에 실제로 들어 있는 값. 칸을 이 값으로 다시 그린다. */
  barcode1: string | null;
}

export const offlineApi = {
  meta: () => api.get<OfflineMeta>('/offline/meta'),

  timetable: (place: string, workdate: string) =>
    api.get<Timetable | null>(
      `/offline/timetable?place=${encodeURIComponent(place)}&workdate=${encodeURIComponent(workdate)}`,
    ),

  start: (place: string, workdate: string, worker1: string, worker2: string) =>
    api.post<Timetable>('/offline/timetable', { place, workdate, worker1, worker2 }),

  checkSlot: (place: string, workdate: string, slot: string) =>
    api.post<{ alreadyDone: boolean; at: string | null }>('/offline/timetable/slot', {
      place, workdate, slot,
    }),

  slotMemo: (place: string, workdate: string, slot: string, memo: string) =>
    api.patch<{ ok: true }>('/offline/timetable/slot', { place, workdate, slot, memo }),

  setBox: (place: string, workdate: string, field: BoxField, value: string) =>
    api.patch<{ ok: true }>('/offline/timetable/box', { place, workdate, field, value }),

  sales: (p: {
    place?: string;
    intype?: string;
    from?: string;
    to?: string;
    weekday?: number | null;
    page?: number;
  }) => {
    const q = new URLSearchParams();
    if (p.place) q.set('place', p.place);
    if (p.intype) q.set('intype', p.intype);
    if (p.from) q.set('from', p.from);
    if (p.to) q.set('to', p.to);
    if (p.weekday !== null && p.weekday !== undefined) q.set('weekday', String(p.weekday));
    if (p.page) q.set('page', String(p.page));
    const qs = q.toString();
    return api.get<SaleList>(`/offline/sales${qs ? `?${qs}` : ''}`);
  },

  voidSale: (seq: number) => api.del<{ ok: true }>(`/offline/sale/${seq}`),

  sale: (place: string, saleDate: string, intype: string) =>
    api.get<Record<string, unknown> | null>(
      `/offline/sale?place=${encodeURIComponent(place)}&saleDate=${encodeURIComponent(saleDate)}&intype=${encodeURIComponent(intype)}`,
    ),

  saveSale: (input: {
    place: string;
    saleDate: string;
    intype: string;
    worker1: string;
    worker2: string;
    inweather: string;
    numbers: Record<string, number>;
  }) => api.post<{ mode: 'insert' | 'update'; seq: number }>('/offline/sale', input),

  /** 탭·버튼·운영시간 안내용. MSSQL 에 붙지 않는 가벼운 호출이다. */
  invenSyncMeta: () => api.get<InvenSyncMeta>('/offline/inven-sync/meta'),

  invenSync: (p: {
    store: string;
    status?: InvenSyncStatus | '';
    q?: string;
    page?: number;
    /** 기본 50. gcode 일괄 적용처럼 한 상태를 통째로 훑을 때 200(서버 상한)까지 올려 받는다. */
    perPage?: number;
    /** 캐시를 버리고 POS 를 다시 읽는다. */
    fresh?: boolean;
  }) => {
    const q = new URLSearchParams({ store: p.store });
    if (p.status) q.set('status', p.status);
    if (p.q) q.set('q', p.q);
    if (p.page) q.set('page', String(p.page));
    if (p.perPage) q.set('perPage', String(p.perPage));
    if (p.fresh) q.set('fresh', '1');
    return api.get<InvenSyncList>(`/offline/inven-sync?${q.toString()}`);
  },

  /**
   * BarCode1 한 줄 수정. `expect` 는 화면이 읽었을 때의 값으로, 그 사이 다른
   * 사람이 고쳤으면 서버가 아무것도 바꾸지 않고 알려준다.
   */
  invenSyncSetBarcode1: (p: {
    store: string;
    posBarcodeStored: string;
    barcode1: string | null;
    expect: string | null;
  }) => api.patch<SetBarcode1Result>('/offline/inven-sync/barcode1', p),

  /**
   * POS 등록용 엑셀 주소 (원본 offline_master_csv.php).
   * 서버가 attachment 로 내려주므로 <a href> 로 걸면 그대로 받아진다.
   */
  posExcelUrl: (p: { catecode?: string; slice?: 1 | 2 } = {}) => {
    const q = new URLSearchParams();
    if (p.catecode) q.set('catecode', p.catecode);
    if (p.slice) q.set('slice', String(p.slice));
    const qs = q.toString();
    return `/api/offline/pos-excel${qs ? `?${qs}` : ''}`;
  },
};
